import React from 'react';
import { Box, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export interface ITip {
    /** What the block / chart shows and which fields feed it. */
    what: string;
    /** How to read it. */
    how?: string;
    /** What a healthy picture looks like. */
    good?: string;
    /** What to worry about. */
    bad?: string;
    /** Extra caveats. */
    note?: string;
}

interface Props {
    tip: ITip;
    size?: 'small' | 'medium';
}

function Row({ label, text, color }: { label: string; text?: string; color?: string }) {
    if (!text) return null;
    return (
        <Box sx={{ mb: 0.75 }}>
            <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, color: color || 'inherit', mr: 0.5, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                {label}
            </Typography>
            <Typography component="span" sx={{ fontSize: 12, lineHeight: 1.45, whiteSpace: 'pre-line' }}>
                {text}
            </Typography>
        </Box>
    );
}

/**
 * Info icon with a rich, multi-section tooltip (what / how to read / good / bad).
 * Opens on hover and on keyboard focus.
 */
export default function MockupGenerationLogsInfoTip({ tip, size = 'small' }: Props) {
    const theme = useTheme();
    const content = (
        <Box sx={{ p: 0.5 }}>
            <Row label="What" text={tip.what} />
            <Row label="How to read" text={tip.how} />
            <Row label="Good" text={tip.good} color="#7fd67f" />
            <Row label="Bad" text={tip.bad} color="#ff9d9d" />
            <Row label="Note" text={tip.note} color={theme.palette.grey[400]} />
        </Box>
    );
    return (
        <Tooltip
            title={content}
            arrow
            placement="top-start"
            enterDelay={150}
            enterTouchDelay={0}
            leaveTouchDelay={6000}
            componentsProps={{
                tooltip: { sx: { maxWidth: 460, bgcolor: 'rgba(20,20,20,0.96)', color: '#f5f5f5', p: 1.25 } },
                arrow: { sx: { color: 'rgba(20,20,20,0.96)' } },
            }}
        >
            <IconButton
                size={size}
                aria-label="Explanation"
                sx={{ p: 0.25, ml: 0.5, color: theme.palette.text.secondary, verticalAlign: 'middle' }}
            >
                <InfoOutlinedIcon sx={{ fontSize: size === 'small' ? 15 : 18 }} />
            </IconButton>
        </Tooltip>
    );
}
